<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';
import { Button, Popover, Checkbox } from 'primevue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import type { ColumnField } from 'shared/common/composables/useColumnSelector';
import { IconSharpDragHandle, IconSharpRefresh } from '@iconify-prerendered/vue-ic';

const props = defineProps<{
  columns: ColumnField[];
  modelValue: ColumnField[];
  variant?: "text" | "outlined" | "link";
  severity?: "success" | "secondary" | "info" | "warn" | "help" | "danger" | "contrast"
  size?: "large" | "small";
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: ColumnField[]): void;
  (event: 'reorder', value: ColumnField[]): void;
  (event: 'reset'): void;
}>();

const { translations } = storeToRefs(useLanguageStore());

const popover = useTemplateRef('popover');

function toggle(event: Event) {
  popover.value?.toggle(event);
}

function isSelected(field: string): boolean {
  return props.modelValue.some((column) => column.field === field);
}

function onToggleColumn(column: ColumnField, checked: boolean) {
  const next = checked
    ? props.columns.filter((candidate) => isSelected(candidate.field) || candidate.field === column.field)
    : props.modelValue.filter((selected) => selected.field !== column.field);
  emit('update:modelValue', next);
}

const dragIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

function onColDragStart(index: number) {
  dragOverIndex.value = index;
  requestAnimationFrame(() => (dragIndex.value = index));
}

function onColDragOver(index: number) {
  if (index !== dragIndex.value) dragOverIndex.value = index;
}

function onColDrop() {
  const from = dragIndex.value;
  const to = dragOverIndex.value;
  if (from !== null && to !== null && from !== to) {
    const next = [...props.columns];
    const [moved] = next.splice(from, 1);
    if (moved) {
      next.splice(to, 0, moved);
      emit('reorder', next);
    }
  }
  onColDragEnd();
}

function onColDragEnd() {
  dragIndex.value = null;
  dragOverIndex.value = null;
}
</script>

<template>
  <Button
    class="column-selector-button"
    type="button"
    icon="pi pi-list"
    :variant="props.variant ?? 'text'"
    :severity="props.severity ?? 'secondary'"
    :size="props.size ?? 'small'"

    v-tooltip.bottom="translations.generic.selectColumns"
    :aria-label="translations.generic.selectColumns"
    @click="toggle"
  />
  <Popover ref="popover" class="w-72" pt:content="p-0!">
    <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-surface-200 dark:border-surface-700">
      <span class="text-sm font-semibold">{{ translations.generic.columns }}</span>
      <Button type="button" variant="text" size="small" severity="secondary" @click="emit('reset')">
        <IconSharpRefresh />
        {{ translations.generic.reset }}
      </Button>
    </div>
    <div class="py-2 max-h-80 overflow-auto" @dragover.prevent @drop="onColDrop">
      <div
        v-for="(column, index) of columns"
        :key="column.field"
        class="flex items-center gap-2 px-3 py-1.5 mx-1 rounded-md cursor-move select-none transition"
        :class="[
          dragIndex === index ? 'opacity-40' : '',
          dragOverIndex === index && dragIndex !== index
            ? 'bg-primary-50 dark:bg-primary-900/30 ring-1 ring-primary-400'
            : 'hover:bg-surface-100 dark:hover:bg-surface-800'
        ]"
        draggable="true"
        @dragstart="onColDragStart(index)"
        @dragover.prevent="onColDragOver(index)"
        @dragend="onColDragEnd"
      >
        <span class="flex text-surface-400 dark:text-surface-500">
          <IconSharpDragHandle />
        </span>
        <Checkbox
          :inputId="`column-selector-${column.field}`"
          :modelValue="isSelected(column.field)"
          binary
          @update:modelValue="(checked: boolean) => onToggleColumn(column, checked)"
        />
        <label :for="`column-selector-${column.field}`" class="text-sm cursor-pointer">{{ column.header }}</label>
      </div>
    </div>
  </Popover>
</template>
